import React, { useEffect, useState } from 'react';
import { 
  Terminal, 
  ShieldCheck, 
  Cpu, 
  Tractor, 
  GraduationCap, 
  Building2, 
  Layers, 
  Globe, 
  Zap 
} from 'lucide-react';

const AboutUs = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const mindslogWork = {
    current: [
      { category: "Global Intelligence", title: "Daily Job Pulse", desc: "Live tracking of AI & Remote roles across USA and India markets." },
      { category: "Curation", title: "AI Tools & Innovation", desc: "Aggregating the top 1% of AI tools and daily breakthroughs in LLMs." },
      { category: "Multi-Niche Blogging", title: "The Knowledge Base", desc: "Deep-dives into Tech, Finance, High-Value Farming, and Non-Tech lifestyle." }
    ],
    future: [
      { title: "ViralFlow AI", desc: "Autonomous content pipelines for creators using multi-agent logic." },
      { title: "Agent Marketplace", desc: "A hub to buy/sell custom-built Python tools and RAG agents." },
      { title: "Zenith Layer", desc: "AI-driven anti-addiction feed filtering for digital wellness." }
    ]
  };

  return (
    <div className="bg-[#020408] text-slate-100 font-sans selection:bg-cyan-500/30 overflow-x-hidden">
      
      {/* =========================================================
          UNIVERSAL BACKGROUND LAYER (Stars, Orbits, AI Glow)
          ========================================================= */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 opacity-30" 
             style={{ backgroundImage: `radial-gradient(white 1px, transparent 0)`, backgroundSize: '60px 60px' }}></div>
        
        {/* Animated Orbits */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vw] h-[120vw]">
          <div className="absolute inset-0 border border-cyan-500/5 rounded-full animate-[spin_120s_linear_infinite]"></div>
          <div className="absolute inset-[25%] border border-blue-500/5 rounded-full animate-[spin_180s_linear_reverse_infinite]"></div>
        </div>

        {/* Glow Blobs */}
        <div className="absolute top-[10%] right-[15%] w-96 h-96 bg-blue-600/5 blur-[120px] rounded-full"></div>
        <div className="absolute bottom-[20%] left-[10%] w-[500px] h-[500px] bg-cyan-600/5 blur-[150px] rounded-full"></div>
      </div>

      {/* =========================================================
          SECTION 1: IDENTITY (THE PERSONNEL)
          ========================================================= */}
      <section className="relative h-screen w-full flex flex-col justify-center items-center z-10 text-center px-4">
        <p className="text-cyan-400 font-mono tracking-[0.5em] text-[10px] uppercase mb-6 animate-pulse">
          Personnel Registry Identified // MindsLog 4.0
        </p>
        <h1 className="text-7xl md:text-[11rem] font-black tracking-tighter leading-none italic mb-16">
          MINDS<span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">LOG</span>
        </h1>

        <div className="flex flex-col md:flex-row gap-12 items-center">
          {/* DEVELOPER: K K NIRALA */}
          {/* <div className="group space-y-3">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.3em]">Developed By</p>
            <div className="flex items-center gap-4 justify-center">
               <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-2xl group-hover:bg-cyan-500/10 group-hover:border-cyan-500/40 transition-all duration-500">
                  <Terminal size={28} className="text-cyan-400" />
               </div>
               <div className="text-left">
                  <h2 className="text-3xl font-bold text-white uppercase italic leading-none">K K Nirala</h2>
                  <p className="text-cyan-500 font-mono text-[10px] uppercase tracking-widest mt-1">Sr. Software Engineer // IBM ISL</p>
               </div>
            </div>
          </div> */}
          
          <div className="w-[1px] h-20 bg-white/10 hidden md:block"></div>

          {/* MAINTAINER: LALITA YADAV */}
          {/* <div className="group space-y-3">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.3em]">Maintained By</p>
            <div className="flex items-center gap-4 justify-center">
               <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-2xl group-hover:bg-blue-500/10 group-hover:border-blue-500/40 transition-all duration-500">
                  <ShieldCheck size={28} className="text-blue-400" />
               </div>
               <div className="text-left">
                  <h2 className="text-3xl font-bold text-white uppercase italic leading-none">Lalita Yadav</h2>
                  <p className="text-blue-500 font-mono text-[10px] uppercase tracking-widest mt-1">Systems Integrity & Ops</p>
               </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* =========================================================
          SECTION 2: ORIGIN PROTOCOL (LIFE STRUGGLE)
          ========================================================= */}
      <section className="relative min-h-screen py-32 bg-[#0f0909]/60 backdrop-blur-md z-10 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-5xl font-black italic mb-24 text-center uppercase tracking-tighter">
            ORIGIN <span className="text-cyan-500 text-glow">PROTOCOL</span>
          </h2>
          
          <div className="space-y-24 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-[1px] before:bg-gradient-to-b before:from-transparent before:via-white/10 before:to-transparent">
            
            {/* Milestone 1: Farmer Background */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
              <div className="flex items-center justify-center w-14 h-14 rounded-full border border-white/10 bg-black z-10 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_20px_rgba(255,255,255,0.05)]">
                 <Tractor size={24} className="text-amber-600" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[45%] p-10 bg-slate-900/40 border border-white/5 rounded-[2.5rem] group-hover:border-amber-500/30 transition-all duration-500">
                <div className="text-amber-500 font-mono text-[10px] font-black uppercase tracking-widest mb-3">Phase_01: The Soil</div>
                <h4 className="text-2xl font-bold text-white mb-4 italic uppercase">Farmer Background</h4>
                <p className="text-slate-400 text-sm leading-relaxed font-light">
                  Born into a farming lineage in Bihar. Before learning to write code, I learned to read the seasons and the soil. The discipline of the harvest—patience, raw grit, and resilience—remains the core logic of my engineering mindset.
                </p>
              </div>
            </div>

            {/* Milestone 2: Small City Transition */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
              <div className="flex items-center justify-center w-14 h-14 rounded-full border border-white/10 bg-black z-10 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                 <Building2 size={24} className="text-slate-400" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[45%] p-10 bg-slate-900/40 border border-white/5 rounded-[2.5rem] group-hover:border-slate-500/30 transition-all duration-500">
                <div className="text-slate-400 font-mono text-[10px] font-black uppercase tracking-widest mb-3">Phase_02: Transition</div>
                <h4 className="text-2xl font-bold text-white mb-4 italic uppercase">Small City Education</h4>
                <p className="text-slate-400 text-sm leading-relaxed font-light">
                  Migrating to a small city to seek a better future. Navigating the friction of limited resources and building my first technical foundations through sheer determination and self-study.
                </p>
              </div>
            </div>

            {/* Milestone 3: NIT Warangal */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
              <div className="flex items-center justify-center w-14 h-14 rounded-full border border-cyan-500/30 bg-black z-10 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
                 <GraduationCap size={24} className="text-cyan-400" />
              </div>
              {/* <div className="w-[calc(100%-4rem)] md:w-[45%] p-10 bg-cyan-950/10 border border-cyan-500/20 rounded-[2.5rem] group-hover:border-cyan-400/50 transition-all duration-500">
                <div className="text-cyan-400 font-mono text-[10px] font-black uppercase tracking-widest mb-3">Phase_03: System Elevation</div>
                <h4 className="text-2xl font-bold text-white mb-4 italic uppercase">NIT Warangal</h4>
                <p className="text-slate-200 text-sm leading-relaxed font-light">
                  Achieving the dream. My MCA from NIT Warangal was the critical upgrade that bridged rural perseverance with high-level architectural intelligence. It was here that my passion for full-stack ecosystems was forged.
                </p>
              </div> */}
            </div>

            {/* Milestone 4: Corporate Excellence */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
              <div className="flex items-center justify-center w-14 h-14 rounded-full border border-blue-500/30 bg-black z-10 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                 <Cpu size={24} className="text-blue-500" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[45%] p-10 bg-blue-950/10 border border-blue-500/20 rounded-[2.5rem] group-hover:border-blue-400/50 transition-all duration-500">
                <div className="text-blue-400 font-mono text-[10px] font-black uppercase tracking-widest mb-3">Phase_04: Global Impact</div>
                <h4 className="text-2xl font-bold text-white mb-4 italic uppercase">Corporate IT Architecture</h4>
                <p className="text-slate-400 text-sm leading-relaxed font-light">
                  Now leading Agentic AI workflow. With 10+ years of experience across MERN and MEAN stacks, I architect autonomous systems that bridge the gap between human intent and machine execution.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================================
          SECTION 3: THE MINDSLOG ENGINE (DIGITAL ECOSYSTEM)
          ========================================================= */}
      <section className="relative min-h-screen py-32 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20 text-center">
            <h2 className="text-5xl md:text-7xl font-black italic mb-4 uppercase tracking-tighter">MINDS<span className="text-cyan-400">LOG</span>.COM</h2>
            <p className="text-slate-500 font-mono uppercase tracking-[0.3em] text-[10px] italic">// Digital Ecosystem Infrastructure</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* The Foundation */}
            <div className="lg:col-span-8 space-y-12">
              <h3 className="text-2xl font-bold flex items-center gap-4 uppercase tracking-tighter text-cyan-400">
                <Layers size={24} /> THE FOUNDATION
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mindslogWork.current.map((work, i) => (
                  <div key={i} className={`p-8 bg-slate-900/40 border border-white/5 rounded-3xl hover:border-cyan-500/30 transition-all duration-500 ${i===2 ? 'md:col-span-2' : ''}`}>
                    <div className="text-cyan-500 text-[9px] font-mono mb-2 uppercase tracking-widest">{work.category}</div>
                    <h4 className="text-xl font-bold mb-3 uppercase italic tracking-tight">{work.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{work.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Future Roadmap */}
            <div className="lg:col-span-4 space-y-12">
              <h3 className="text-2xl font-bold flex items-center gap-4 uppercase tracking-tighter text-blue-400">
                <Zap size={24} /> ROADMAP
              </h3>
              <div className="space-y-4">
                {mindslogWork.future.map((item, i) => (
                  <div key={i} className="group p-6 bg-blue-500/5 border border-white/5 rounded-2xl hover:bg-blue-500/10 hover:border-blue-500/30 transition-all duration-500">
                    <h5 className="text-xs font-black uppercase tracking-widest group-hover:text-blue-400">{item.title}</h5>
                    <p className="text-[11px] text-slate-500 italic mt-2 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          SECTION 4: CALL TO ACTION (FINAL SYNC)
          ========================================================= */}
      <section className="h-screen flex flex-col justify-center items-center px-6 text-center z-10 bg-gradient-to-t from-[#0f0909] to-transparent">
        <h2 className="text-6xl md:text-[9rem] font-black mb-10 italic leading-none">BUILD THE <span className="text-cyan-500 text-glow">FUTURE.</span></h2>
        <p className="max-w-2xl mx-auto text-slate-400 mb-14 text-lg font-light italic">
            "Your origin determines where you start, but your architecture determines how far you go."
        </p>
        <div className="flex flex-col md:flex-row gap-6">
            <a href="/contact-us" className="px-14 py-5 bg-white text-black font-black rounded-full hover:bg-cyan-400 transition-all transform hover:scale-110 shadow-[0_0_60px_rgba(34,211,238,0.25)] flex items-center gap-3">
                <Globe size={18} /> ESTABLISH CONNECTION
            </a>
            <button onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="px-14 py-5 border border-white/10 font-bold rounded-full hover:bg-white/5 transition-all text-slate-500 uppercase text-xs tracking-[0.2em]">
                RE-SYNC SEQUENCE
            </button>
        </div>
      </section>

      {/* PERSISTENT SYSTEM LABEL */}
      <div className="fixed bottom-6 right-6 font-mono text-[9px] text-slate-800 hidden md:block select-none pointer-events-none z-50 uppercase tracking-[0.3em]">
        SYSTEM_PROD_4.0 // ARCHITECT: K.K. NIRALA // MAINT: L. YADAV
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .text-glow { text-shadow: 0 0 30px rgba(34, 211, 238, 0.4); }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}} />
    </div>
  );
};

export default AboutUs;